import { NgFor, NgIf } from "@angular/common";
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PopperHelper } from "@nts/std";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { Subject, debounceTime } from "rxjs";
import { PageFormatType, PageOrientationType } from "src/app/sign-template-long-op/view-models/signer-layout.collection-item-view-model";

export interface RectInterface {
    /**
     * Posizione left in px
     */
    left: number,

    /**
     * Larghezza in px
     */
    width: number,

    /**
     * Posizione top in px
     */
    top: number,

    /**
     * Altezza in px
     */
    height: number,
}

// export interface PageSettings {
//     pageOrientation: PageOrientationType,
//     pageFormat: PageFormatType;
//     customPageHeight: number;
//     customPageWidth: number;
// }

@UntilDestroy()
@Component({
    selector: 'nts-custom-blank-editor',
    templateUrl: './custom-blank-editor.component.html',
    styleUrls: ['./custom-blank-editor.component.scss'],
    standalone: true,
    imports: [
        NgxPopperjsModule,
        NgIf,
        NgFor
    ]
})
export class CustomBlankEditorComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

    // @Input() pageSettings: PageSettings = null;
    /**
     * Orientamento pagina
     */
    @Input() pageOrientation: PageOrientationType = PageOrientationType.Vertical;

    /**
     * Formato pagina
     */
    @Input() pageFormat: PageFormatType;

    /**
     * Gestione errori
     */
    @Input() showErrorTooltip = true;
    @Input() showErrorBorder = true;
    @Input() errorList: string[] = [];

    /**
     * Top iniziale del rettangolo in PDF point
     */
    @Input() topPdfPoint: number = null;

    /**
     * Left iniziale del rettangolo in PDF point
     */
    @Input() leftPdfPoint: number = null;

    /**
     * Altezza personalizzata in mm
     */
    @Input() customPageHeight: number = 0;

    /**
     * Larghezza personalizzata in mm
     */
    @Input() customPageWidth: number = 0;

    /**
     * Placeholder da visualizzare quando non sono state passati i pdf point
     */
    @Input() placeholder: string = ''

    /**
     * Altezza fissa rettangolo in mm
     */
    @Input() rectangleHeight: number = 17;

    /**
     * Larghezza fissa rettangolo in mm
     */
    @Input() rectangleWidth: number = 60;

    /**
     * Altezza fissa del container del canvas
     */
    @Input() initialWrapperHeight = null;

    /**
     * Larghezza fissa del container del canvas
     */
    @Input() initialWrapperWidth = null;

    /**
     * Qaundo si sposta il rettangolo o si resettano i point viene emesso questo evento
     */
    @Output() pdfPointChanged: EventEmitter<{ topPdfPoint: number, leftPdfPoint: number }> = new EventEmitter();

    @Output() onUpdateRectDimension: EventEmitter<{
        /**
         * Altezza in pixel
         */
        height: number,

        /**
         * Lunghezza in pixel
         */
        width: number
    }> = new EventEmitter();

    @ViewChild('canvasWrapper') canvasWrapper: NgxPopperjsDirective;
    @ViewChild('queryCanvas') queryCanvas: ElementRef;

    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;

    tallsX = Array(25);
    tallsY = Array(25);

    /**
     * Altezza calcolata del rettangolo in mm
     */
    calculatedRectangleWidth: number = this.rectangleWidth;

    /**
     * Larghezza calcolata del rettangolo in mm
     */
    calculatedRectangleHeight: number = this.rectangleHeight;

    /**
     * 1 mm = 2.8346456692912997 PDF Point
     */
    pdfPointConversionRatio = 2.8346456692912997;

    /**
     *  Debouncer per l'evento pdfPointChanged
     */
    pdfPointChangedDebouncer: Subject<{
        leftPdfPoint: number,
        topPdfPoint: number
    }> = new Subject();

    /**
     * Coordinata X del mouse da quando è iniziato il mousedown
     */
    mouseDownStartX = null;

    /**
     * Coordinata Y del mouse da quando è iniziato il mousedown
     */
    mouseDownStartY = null;

    /**
     * Coordinata X del mouse quando è sopra il container del canvas
     */
    mouseX = null;

    /**
     * Coordinata Y del mouse quando è sopra il container del canvas
     */
    mouseY = null;

    /**
     * Verifica se si sta facendo il drag con il mouse
     */
    isDragging = false;

    /**
     * Larghezza pagina in mm considerando dopo aver applicato l'orientamento
     */
    pageWidthWithOrentation: number = 0;

    /**
     * Altezza pagina in mm considerando dopo aver applicato l'orientamento
     */
    pageHeightWithOrentation: number = 0;

    /**
     * Ultima altezza in px del container del canvas
     */
    lastWrapperHeight = null;

    /**
     * Ultima larghezza in px del container del canvas
     */
    lastWrapperWidth = null;

    /**
     * Observer del cambio delle dimensioni del canvas
     */
    canvasWrapperResizeObserver: ResizeObserver = null;

    // Coordinate di partenza riquadro in mm
    th_left = this.leftPdfPoint / this.pdfPointConversionRatio;
    th_top = this.topPdfPoint / this.pdfPointConversionRatio;
    th_right = this.th_left + this.calculatedRectangleWidth;
    th_bottom = this.th_top + this.calculatedRectangleHeight;

    // Dimensioni riquadro in mm
    th_width = this.th_right - this.th_left;
    th_height = this.th_bottom - this.th_top;

    rect: RectInterface = {
        left: null,
        width: null,
        top: null,
        height: null,
    };

    /**
     * Left corrente in mm del rettangolo
     */
    currentRectangleLeftMM = 0;

    /**
    * Top corrente in mm del rettangolo
    */
    currentRectangleTopMM = 0;

    /**
    * Right corrente in mm del rettangolo
    */
    currentRectangleRightMM = 0;

    /**
    * Bottom corrente in mm del rettangolo
    */
    currentRectangleBottomMM = 0;

    /**
     * Rapporto di conversione per il righello
     */
    rulerConversionRatio = 0;

    constructor() {
        this.pdfPointChangedDebouncer.pipe(untilDestroyed(this), debounceTime(100)).subscribe((value) => {
            this.pdfPointChanged.emit(value)
        })
    }

    ngOnInit(): void {

    }

    ngAfterViewInit(): void {
        const canvasWrapperResizeObserver = new ResizeObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.contentBoxSize && entry.contentBoxSize[0].blockSize > 0) {
                        if (!this.lastWrapperHeight && !this.lastWrapperWidth) {
                            this.lastWrapperWidth = entry.contentBoxSize[0].inlineSize;
                            this.lastWrapperHeight = entry.contentBoxSize[0].blockSize;
                            this.initCanvas();
                        }
                        if (!this.lastWrapperHeight) {
                            this.lastWrapperHeight = entry.contentBoxSize[0].blockSize;
                            this.initCanvas();
                        }
                        if (!this.lastWrapperWidth) {
                            this.lastWrapperWidth = entry.contentBoxSize[0].inlineSize;
                            this.initCanvas();
                        }
                        if (
                            entry.contentBoxSize[0].blockSize != this.lastWrapperHeight ||
                            entry.contentBoxSize[0].inlineSize != this.lastWrapperWidth
                        ) {
                            this.initCanvas();
                        }
                        this.lastWrapperHeight = entry.contentBoxSize[0].blockSize;
                        this.lastWrapperWidth = entry.contentBoxSize[0].inlineSize;
                    }
                }
            }
        )
        canvasWrapperResizeObserver.observe(this.canvasWrapper?.getRefElement());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['errorList']) {
            this.checkPopper();
            //     return;
        }
        if (this.canvasWrapper &&
            (
                changes['pageOrientation'] ||
                changes['pageFormat'] ||
                changes['topPdfPoint'] ||
                changes['leftPdfPoint'] ||
                changes['customPageHeight'] ||
                changes['rectangleHeight'] ||
                changes['rectangleWidth'] ||
                changes['initialWrapperHeight'] ||
                changes['initialWrapperWidth']
            )
        ) {
            this.initCanvas();
        }
    }

    ngOnDestroy(): void {
        if (this.canvasWrapper?.getRefElement() && this.canvasWrapperResizeObserver) {
            this.canvasWrapperResizeObserver.unobserve(this.canvasWrapper.getRefElement());
        }
    }

    onMouseUp(e: TouchEvent | MouseEvent): void {
        const canvas = this.queryCanvas.nativeElement;
        this.isDragging = false;
        canvas.className = '';
    }

    /**
     * Aggiorna la posizione e dimensione del rettangolo e lo disegna durante lo spostamento del mouse
     *
     * @param e     mouse event
     */
    onMouseMove(e: TouchEvent | MouseEvent): void {

        const canvas: any = this.queryCanvas.nativeElement;
        const pos = this.getMousePos(canvas, e);
        this.mouseX = pos.x;
        this.mouseY = pos.y;
        if (this.isDragging) {
            e.preventDefault();
            e.stopPropagation();
            const draggingX = this.mouseX - this.mouseDownStartX;
            const draggingY = this.mouseY - this.mouseDownStartY;
            if (
                this.rect.left + draggingX > 0 &&
                this.rect.left + draggingX + this.rect.width < canvas.width
            ) {
                this.rect.left += draggingX;
            }
            if (
                this.rect.top + draggingY > 0 &&
                this.rect.top + draggingY + this.rect.height < canvas.height
            ) {
                this.rect.top += draggingY;
            }
            this.mouseDownStartX = this.mouseX;
            this.mouseDownStartY = this.mouseY;
        } else if (this.checkInRect(this.mouseX, this.mouseY, this.rect)) {
            canvas.className = 'drag';
        } else {
            canvas.className = '';
        }
        this.drawRectInCanvas();
    }

    onMouseDown(e: TouchEvent | MouseEvent): void {
        const canvas = this.queryCanvas.nativeElement;
        const pos = this.getMousePos(canvas, e);
        this.mouseX = pos.x;
        this.mouseY = pos.y;

        if (this.checkInRect(this.mouseX, this.mouseY, this.rect)) {
            this.isDragging = true;
            this.mouseDownStartX = this.mouseX;
            this.mouseDownStartY = this.mouseY;
            canvas.className = 'drag';
        }
        this.drawRectInCanvas();
    }

    /**
     * Disegna il rettangolo nel canvas
     */
    private drawRectInCanvas() {
        if (this.topPdfPoint >= 0 && this.topPdfPoint != null && this.leftPdfPoint >= 0 && this.leftPdfPoint != null) {
            const canvas = this.queryCanvas.nativeElement;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.beginPath();
            ctx.lineWidth = '3';
            ctx.fillStyle = 'rgba(199, 87, 231, 0.2)';
            ctx.strokeStyle = '#c757e7';
            ctx.rect(this.rect.left, this.rect.top, this.rect.width, this.rect.height);
            ctx.fill();
            ctx.stroke();
            // this.drawHandles();
            // this.drawRuler();
            this.updateStatistics();
        }
    }

    /**
     * Recupera le coordinate x e y del mouse relative al canvas
     * @param canvas    canvas
     * @param evt       evento touch o del mouse
     * @returns         coordinate x e y
     */
    private getMousePos(canvas: any, evt: TouchEvent | MouseEvent): {
        x: number,
        y: number
    } {
        let clientX: number, clientY: number;
        if (evt.type == 'touchstart' || evt.type == 'touchmove') {
            clientX = (evt as TouchEvent).touches[0].clientX;
            clientY = (evt as TouchEvent).touches[0].clientY;
        } else {
            clientX = (evt as MouseEvent).clientX;
            clientY = (evt as MouseEvent).clientY;
        }
        var boundingRect = canvas.getBoundingClientRect();
        return {
            x: clientX - boundingRect.left,
            y: clientY - boundingRect.top,
        };
    }

    /**
     * Verifica che le coordinate passate sia dentro le coordinate del rettangolo passate
     *
     * @param x coordinata x da verificare
     * @param y coordinata y da verificare
     * @param r coordinate rettangolo
     * @returns verso se sono interno altrimenti falso
     */
    private checkInRect(x: number, y: number, r: RectInterface): boolean {
        return (
            x > r.left && x < r.width + r.left && y > r.top && y < r.top + r.height
        );
    }

    /**
     * Aggiorna le varabili per le statistiche
     */
    private updateStatistics() {
        const canvas = this.queryCanvas.nativeElement;
        const inverse_ratio_w = this.pageWidthWithOrentation / canvas.width;
        const inverse_ratio_h = this.pageHeightWithOrentation / canvas.height;
        this.currentRectangleLeftMM = Math.round(this.rect.left * inverse_ratio_w);
        this.currentRectangleTopMM = Math.round(this.rect.top * inverse_ratio_h);
        this.currentRectangleRightMM = Math.round(
            (this.rect.left + this.rect.width) * inverse_ratio_w
        );
        this.currentRectangleBottomMM = Math.round(
            (this.rect.top + this.rect.height) * inverse_ratio_h
        );

        this.leftPdfPoint = this.currentRectangleLeftMM * this.pdfPointConversionRatio;
        this.topPdfPoint = this.currentRectangleTopMM * this.pdfPointConversionRatio;

        this.pdfPointChangedDebouncer.next({
            leftPdfPoint: this.leftPdfPoint,
            topPdfPoint: this.topPdfPoint
        });

    }

    /**
     * Verifica e aggiorna le dimensioni della pagina in base al formato
     */
    private checkDimension() {
        switch (this.pageFormat) {
            case PageFormatType.A3:
                this.pageWidthWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? 297 : 420;
                this.pageHeightWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? 420 : 297;
                break;

            case PageFormatType.A4:
                this.pageWidthWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? 210 : 297;
                this.pageHeightWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? 297 : 210;
                break;

            case PageFormatType.A5:
                this.pageWidthWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? 148 : 210;
                this.pageHeightWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? 148 : 210;
                break;

            case PageFormatType.Custom:
                this.pageWidthWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? this.customPageWidth : this.customPageHeight;
                this.pageHeightWithOrentation = this.pageOrientation == PageOrientationType.Vertical ? this.customPageHeight : this.customPageWidth;
                break;
        }
    }

    private adaptRectangleSize() {

        // Riadatto il rettangolo
        if (this.rectangleHeight > this.pageHeightWithOrentation) {
            this.calculatedRectangleHeight = this.pageHeightWithOrentation - 2;
        } else {
            this.calculatedRectangleHeight = this.rectangleHeight;
        }
        if (this.rectangleWidth > this.pageWidthWithOrentation) {
            this.calculatedRectangleWidth = this.pageWidthWithOrentation - 2;
        } else {
            this.calculatedRectangleWidth = this.rectangleWidth;
        }
    }

    private initRect() {
        const canvas = this.queryCanvas.nativeElement;
        const ratio_w = canvas.width / this.pageWidthWithOrentation;
        const ratio_h = canvas.height / this.pageHeightWithOrentation;
        this.rect.height = this.th_height * ratio_h;
        this.rect.width = this.th_width * ratio_w;
        this.onUpdateRectDimension.emit({
            height: this.rect.height,
            width: this.rect.width
        });
        this.rect.top = this.th_top * ratio_h;
        this.rect.left = this.th_left * ratio_w;
    }

    private updateRectFromPdfPoints() {
        this.th_left = this.leftPdfPoint / this.pdfPointConversionRatio;
        this.th_top = this.topPdfPoint / this.pdfPointConversionRatio;
        this.th_right = this.th_left + this.calculatedRectangleWidth;
        this.th_bottom = this.th_top + this.calculatedRectangleHeight;

        // Dimensioni riquadro in mm
        this.th_width = this.th_right - this.th_left;
        this.th_height = this.th_bottom - this.th_top;
    }

    // private initRulez(canvas) {
    //     this.rulez = new Rulez.default({
    //         element: canvas,
    //     });
    // }

    private updateRuler() {
        const canvas = this.queryCanvas.nativeElement;
        this.rulerConversionRatio = canvas.height / this.pageHeightWithOrentation;
    }

    createRect() {
        this.topPdfPoint = 0;
        this.leftPdfPoint = 0;
        this.initCanvas();
    }

    private checkPopper() {
        if (this.errorList.length === 0) {
            PopperHelper.hide(this.canvasWrapper);
        }
    }

    private initCanvas() {
        this.checkPopper();
        this.checkDimension();

        const canvas = this.queryCanvas.nativeElement;

        if (this.initialWrapperHeight > 0) {
            canvas.height = (this.initialWrapperHeight)
                - 20 // ruler
                - 2; // canvas border
            canvas.width = canvas.height * this.pageWidthWithOrentation / this.pageHeightWithOrentation;
        } else if (this.initialWrapperWidth > 0) {
            canvas.width = (this.initialWrapperWidth)
                - 20 // ruler
                - 2; // canvas border
            canvas.height = canvas.width * this.pageHeightWithOrentation / this.pageWidthWithOrentation;
        } else {
            this.initialWrapperHeight = 500;
            canvas.height = (this.initialWrapperHeight)
                - 20 // ruler
                - 2; // canvas border
            canvas.width = canvas.height * this.pageWidthWithOrentation / this.pageHeightWithOrentation;
        }

        this.updateRuler();

        if (this.topPdfPoint >= 0 && this.topPdfPoint != null && this.leftPdfPoint >= 0 && this.leftPdfPoint != null) {
            this.adaptRectangleSize();
            this.updateRectFromPdfPoints();
            this.initRect();
            this.drawRectInCanvas();
        } else {
            this.pdfPointChangedDebouncer.next({
                leftPdfPoint: null,
                topPdfPoint: null
            });
        }

        // const hrulerCanvas = document.getElementById('hruler-canvas') as any;
        // hrulerCanvas.width = canvasWrapper.offsetWidth;
        // hrulerCanvas.height = 20;

        // const vrulerCanvas = document.getElementById('vruler-canvas') as any;
        // vrulerCanvas.width = 20;
        // vrulerCanvas.height = canvasWrapper.offsetHeight;

        // this.effective_image_width = canvas.width;
        // this.effective_image_height = canvas.height;
        // canvas.style.top = canvasWrapper.offsetTop + 'px';
        // canvas.style.left = canvasWrapper.offsetLeft + 'px';
        // this.updateCurrentCanvasRect();
    }

    // var cmm=15
    // var countCm=0

    // drawRuler() {

    //     var cmm = 15
    //     var countCm = 0
    //     const c = this.queryCanvas.nativeElement
    //     var ppm = 8.602150744349842,
    //         count = Math.floor(c.height / ppm);

    //     for (var i = 0; i < count; i++) {
    //         if (countCm == 4) { cmm = 15; countCm = 0 } else { cmm = 4; countCm++ }
    //         var topPos = (5 + i * ppm);
    //         this.drawOnCanvas(Math.floor(topPos) + .5);
    //     };

    // }

    // drawOnCanvas(y) {
    //     var cmm = 15
    //     const c = this.queryCanvas.nativeElement
    //     var ctx = c.getContext("2d");
    //     ctx.beginPath();
    //     ctx.moveTo(0, y);
    //     ctx.lineTo(cmm, y);
    //     ctx.stroke();
    // }
}
